import React from "react";
import { Box, Chip, LinearProgress } from "@mui/material";
import { Token } from "../../theme";

interface Props {
  label: string;
  value: string | number;
  suffix?: string;
  detail?: string;
  icon?: React.ReactNode;
  iconColor?: string;
  /** 0–100 progress for logged-hours style KPIs */
  progress?: number;
  chips?: string[];
  onClick?: () => void;
}

export const KpiTile: React.FC<Props> = ({ label, value, suffix, detail, icon, iconColor, progress, chips, onClick }) => {
  const interactive = Boolean(onClick);

  return (
    <Box
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        background: Token.surfaceCard,
        border: `1px solid ${Token.borderDefault}`,
        borderRadius: "8px",
        padding: "14px 12px",
        cursor: interactive ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minHeight: 0,
        transition: interactive ? "background-color 120ms ease, border-color 120ms ease" : undefined,
        ...(interactive && {
          "&:hover": {
            backgroundColor: Token.surfaceHover,
            borderColor: Token.borderStrong,
          },
          "&:focus-visible": {
            outline: `2px solid ${Token.brandAccent}`,
            outlineOffset: 2,
          },
        }),
      }}
    >
      <Box style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div
          style={{
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            color: Token.textTertiary,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
        {icon && (
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              color: iconColor ?? Token.textTertiary,
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          marginTop: 6,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1.1,
            color: Token.textPrimary,
          }}
        >
          {value}
        </span>
        {suffix && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.2,
              color: Token.textTertiary,
            }}
          >
            {suffix}
          </span>
        )}
      </div>

      {typeof progress === "number" && (
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, progress))}
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 3,
            backgroundColor: Token.surfaceSunken,
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: Token.brandAccent,
            },
          }}
        />
      )}

      {chips && chips.length > 0 && (
        <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {chips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              size="small"
              sx={{
                height: 22,
                borderRadius: "6px",
                fontSize: 11,
                fontWeight: 500,
                backgroundColor: Token.statusWarningBg,
                color: Token.statusWarningFg,
                cursor: interactive ? "pointer" : "default",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          ))}
        </Box>
      )}

      {detail && <div style={{ fontSize: 11, color: Token.textTertiary, marginTop: chips?.length ? 8 : 4 }}>{detail}</div>}
    </Box>
  );
};
